import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth, ROLE_LABELS } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit2, Trash2, Users as UsersIcon, Shield, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_OPTIONS = ['cocina', 'caixa', 'admin_empresa', 'admin_ferramenta'];

export default function Users() {
  const { user, lang } = { ...useAuth(), ...useI18n() };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', nombre: '', role: 'caixa', activo: true });

  const load = useCallback(async () => {
    const r = await api.get('/users');
    setItems(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ email: '', password: '', nombre: '', role: 'caixa', activo: true });
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, password: '', nombre: u.nombre, role: u.role, activo: u.activo });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        const payload = { email: form.email, nombre: form.nombre, role: form.role, activo: form.activo };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success(lang === 'es' ? 'Usuario actualizado' : 'Usuário atualizado');
      } else {
        if (!form.password || form.password.length < 8) {
          toast.error(lang === 'es' ? 'Contraseña: mínimo 8 caracteres' : 'Senha: mínimo 8 caracteres');
          return;
        }
        await api.post('/users', form);
        toast.success(lang === 'es' ? 'Usuario creado' : 'Usuário criado');
      }
      setOpen(false);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  const remove = async (u) => {
    if (u.id === user?.id) {
      toast.error(lang === 'es' ? 'No puedes eliminar tu propio usuario' : 'Não pode eliminar seu próprio usuário');
      return;
    }
    if (!window.confirm(`${lang === 'es' ? 'Eliminar' : 'Eliminar'} ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(lang === 'es' ? 'Usuario eliminado' : 'Usuário eliminado');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const labels = ROLE_LABELS[lang] || ROLE_LABELS.es;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <UsersIcon className="h-6 w-6 text-[hsl(var(--primary))]" />
            {lang === 'es' ? 'Gestión de usuarios' : 'Gestão de usuários'}
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            {lang === 'es'
              ? '4 grupos: Cocina · Caja · Admin del restaurante · Super-admin'
              : '4 grupos: Cozinha · Caixa · Admin do restaurante · Super-admin'}
          </p>
        </div>
        <Button onClick={openNew} data-testid="users-new-button">
          <Plus className="h-4 w-4 mr-2" />
          {lang === 'es' ? 'Nuevo usuario' : 'Novo usuário'}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--muted))]/50">
              <tr className="text-left">
                <th className="p-3">{lang === 'es' ? 'Nombre' : 'Nome'}</th>
                <th className="p-3">Email</th>
                <th className="p-3">{lang === 'es' ? 'Rol' : 'Papel'}</th>
                <th className="p-3 text-center">{lang === 'es' ? 'Activo' : 'Ativo'}</th>
                <th className="p-3 text-right">{lang === 'es' ? 'Acciones' : 'Ações'}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t hover:bg-[hsl(var(--muted))]/30" data-testid={`user-row-${u.id}`}>
                  <td className="p-3 font-medium">{u.nombre}</td>
                  <td className="p-3 text-[hsl(var(--muted-foreground))]">{u.email}</td>
                  <td className="p-3">
                    <Badge variant="outline" className="gap-1">
                      <Shield className="h-3 w-3" />{labels[u.role] || u.role}
                    </Badge>
                  </td>
                  <td className="p-3 text-center">
                    <Badge variant={u.activo ? 'default' : 'secondary'}>
                      {u.activo ? (lang === 'es' ? 'Activo' : 'Ativo') : (lang === 'es' ? 'Inactivo' : 'Inativo')}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(u)} className="text-red-600" data-testid={`user-delete-${u.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-[hsl(var(--muted-foreground))]">
                  {lang === 'es' ? 'Sin usuarios' : 'Sem usuários'}
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? (lang === 'es' ? 'Editar usuario' : 'Editar usuário') : (lang === 'es' ? 'Nuevo usuario' : 'Novo usuário')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{lang === 'es' ? 'Nombre' : 'Nome'}</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="user-form-nombre" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" />
            </div>
            <div>
              <Label className="flex items-center gap-1">
                <KeyRound className="h-3.5 w-3.5" />
                {editing
                  ? (lang === 'es' ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Nova senha (deixe vazio para não trocar)')
                  : (lang === 'es' ? 'Contraseña (mín. 8 chars)' : 'Senha (mín. 8 chars)')}
              </Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" />
            </div>
            <div>
              <Label>{lang === 'es' ? 'Rol' : 'Papel'}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r} value={r}>{labels[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>{lang === 'es' ? 'Activo' : 'Ativo'}</Label>
              <Switch checked={form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} data-testid="user-form-activo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{lang === 'es' ? 'Cancelar' : 'Cancelar'}</Button>
            <Button onClick={save} data-testid="user-form-save">{lang === 'es' ? 'Guardar' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
